import client, { apiErrorMessage } from './client';
import {
  CourierOption,
  FormulaOil,
  Order,
  OrderStatus,
  ShipmentResult,
  ShippingInfo,
  SyncShipmentResult,
} from '../types/order';
import { FifoFormulationCost } from '../types/inventory';
import { ListResult, normalizeList } from '../utils/list';

export interface OrderListParams {
  status?: string;
  page?: number;
  limit?: number;
  search?: string;
  from?: string;
  to?: string;
  user_id?: string;
}

export async function getOrders(params: OrderListParams = {}): Promise<ListResult<Order>> {
  const limit = params.limit ?? 20;
  const res = await client.get('/api/admin/orders', { params: { ...params, limit } });
  return normalizeList<Order>(res.data, 'orders', limit);
}

export async function getOrder(id: string): Promise<Order> {
  const res = await client.get(`/api/admin/orders/${id}`);
  return res.data?.order ?? res.data?.data ?? res.data;
}

// FIFO raw-material cost of the order's formula, computed server-side against
// the inventory cost layers (each ml priced at the batch it came from).
export async function getOrderCost(id: string): Promise<FifoFormulationCost> {
  const res = await client.get(`/api/admin/orders/${id}/cost`);
  return res.data?.cost ?? res.data;
}

export async function updateStatus(id: string, status: OrderStatus, notes: string): Promise<void> {
  await client.patch(`/api/admin/orders/${id}/status`, { status, notes });
}

export async function updateShipping(id: string, data: ShippingInfo): Promise<void> {
  await client.patch(`/api/admin/orders/${id}/shipping`, data);
}

export async function cancelOrder(id: string, reason: string): Promise<void> {
  await client.post(`/api/admin/orders/${id}/cancel`, { reason });
}

// What editing a formula did to inventory, reported back on every successful
// save. Orders whose oils have already been deducted have their stock moved by
// the difference; orders that never deducted are left alone. Both outcomes are
// stated explicitly so the admin never has to guess which happened.
export interface FormulaSaveResult {
  oils: FormulaOil[];
  inventory?: {
    /** True when stock actually moved. */
    adjusted: boolean;
    /** Signed ml per oil, from inventory's side: + went back on the shelf. */
    deltas: { name: string; ml: number }[];
    /** Ready-to-show sentence describing the outcome. */
    message: string;
  };
}

// A 409 from either of these means the save was REFUSED and nothing changed —
// the error names the oil with insufficient stock. Stock and formula are never
// allowed to disagree, so a formula edit that cannot be matched by an inventory
// adjustment is not saved at all.
export async function updateFormulation(
  id: string,
  oils: FormulaOil[]
): Promise<FormulaSaveResult> {
  const res = await client.patch(`/api/admin/orders/${id}/formulation`, { oils });
  return res.data?.data ?? res.data;
}

// ── Formulated stage ─────────────────────────────────────────────────────────

export interface MarkFormulatedResult {
  /** The refreshed order, exactly as GET /api/admin/orders/:id returns it. */
  data: Order;
  /** Present when the Shiprocket order was created; null when creation failed. */
  shiprocket_order_id: string | null;
  /**
   * Why the Shiprocket order could not be created, or null. The status is saved
   * as 'formulated' either way — the bottle is made regardless of whether a
   * courier API answered — and Ready to Ship creates the order on the next try.
   */
  shipment_error: string | null;
}

// The perfume is compounded and bottled: move the order to 'formulated' and
// register it with Shiprocket (order record only — no courier, no AWB, no label,
// no customer notification).
export async function markFormulated(id: string): Promise<MarkFormulatedResult> {
  const res = await client.post(`/api/admin/orders/${id}/mark-formulated`, {});
  return res.data;
}

// ── Self-ship (delivered by hand) ────────────────────────────────────────────

// Skip Shiprocket entirely: the order goes straight to 'delivered' and the
// customer gets the delivered WhatsApp. Only for paid orders with no Shiprocket
// shipment. Returns the refreshed order.
export async function selfShipOrder(id: string, note?: string): Promise<Order> {
  const res = await client.post(`/api/admin/orders/${id}/self-ship`, note ? { note } : {});
  return res.data?.data ?? res.data;
}

// ── GST invoices (4×6 label stock, generated on demand) ──────────────────────

// Trigger a browser download of a PDF the API returned as a blob. The response
// is binary, so it can't ride the usual JSON path; the object URL is revoked
// straight after the click so the blob isn't held in memory.
function downloadBlob(data: BlobPart, filename: string): void {
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// An error response to a blob request arrives as a Blob, not JSON — so the
// server's message has to be read back out of it before it can be shown.
async function blobErrorMessage(err: unknown): Promise<string> {
  const data = (err as any)?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.error) return String(parsed.error);
    } catch {
      /* not JSON — fall through to the generic message */
    }
  }
  return apiErrorMessage(err);
}

/** Download one order's GST invoice. Throws a ready-to-show message on failure. */
export async function downloadInvoice(id: string): Promise<void> {
  try {
    const res = await client.get(`/api/admin/orders/${id}/invoice`, { responseType: 'blob' });
    downloadBlob(res.data, `TPC-Invoice-${id.slice(-8)}.pdf`);
  } catch (err) {
    throw new Error(await blobErrorMessage(err));
  }
}

export interface BulkInvoiceResult {
  /** How many invoices the downloaded PDF actually contains. */
  count: number;
  /** Orders left out — unpaid, missing, or beyond the 50-per-request cap. */
  skipped: number;
}

/**
 * Download one PDF holding an invoice per order, one per 4×6 page.
 *
 * Pass `{ status: 'formulated' }` for the one-click "print today's batch" — the
 * server then selects every paid formulated order and ignores any ids.
 */
export async function downloadBulkInvoices(
  selector: { orderIds: string[] } | { status: string }
): Promise<BulkInvoiceResult> {
  const params =
    'status' in selector ? { status: selector.status } : { orderIds: selector.orderIds.join(',') };
  try {
    const res = await client.get(`/api/admin/orders/invoices/bulk`, {
      params,
      responseType: 'blob',
    });
    const day = new Date().toISOString().slice(0, 10);
    downloadBlob(res.data, `TPC-Invoices-${day}.pdf`);
    return {
      count: Number(res.headers['x-invoice-count']) || 0,
      skipped: Number(res.headers['x-invoice-skipped']) || 0,
    };
  } catch (err) {
    throw new Error(await blobErrorMessage(err));
  }
}

// ── Shiprocket shipping (admin-triggered) ────────────────────────────────────

// Fetch the couriers serviceable for this order's destination (cheapest first).
// The backend creates the Shiprocket order on first call to get a shipment id.
export async function getCourierRecommendations(id: string): Promise<CourierOption[]> {
  const res = await client.get(`/api/admin/orders/${id}/courier-recommendations`);
  return res.data?.couriers ?? [];
}

// Create a shipment (marks the order ready_to_ship). courierId null →
// Shiprocket auto-assigns the best rate. `items` selects which units go in this
// parcel (partial shipment); omit to ship everything remaining (one-click).
//
// Also the RETRY call: the endpoint is resumable, so re-invoking it on a
// half-created shipment picks up from the step that failed and never repeats a
// billed one. Pass `shipmentId` to retry one specific parcel.
export async function createShipment(
  id: string,
  courierId: number | null,
  items?: { order_item_id: string; units: number }[],
  shipmentId?: string
): Promise<ShipmentResult> {
  const res = await client.post(`/api/admin/orders/${id}/ship`, {
    courier_id: courierId,
    ...(items && items.length ? { items } : {}),
    ...(shipmentId ? { shipment_id: shipmentId } : {}),
  });
  return res.data;
}

// Pull the current Shiprocket state back into our DB — for when the shipment was
// fixed by hand in the Shiprocket dashboard. Never notifies the customer.
export async function syncShipment(id: string): Promise<SyncShipmentResult> {
  const res = await client.post(`/api/admin/orders/${id}/sync-shipment`, {});
  return res.data;
}

// Schedule a courier pickup for one specific shipment of an order. Optional
// pickupDate (YYYY-MM-DD) books a future pickup; omitted → next available slot.
// Always sends a non-empty body (pickup_date key) so the gateway forwards it.
export async function schedulePickup(
  orderId: string,
  shipmentId: string,
  pickupDate?: string | null
): Promise<{ pickup_scheduled: boolean; pickup_scheduled_date: string | null; message: string | null }> {
  const res = await client.post(
    `/api/admin/orders/${orderId}/shipments/${shipmentId}/schedule-pickup`,
    { pickup_date: pickupDate ?? null }
  );
  return res.data;
}

export interface BulkShipResult {
  total: number;
  succeeded: number;
  failed: number;
  results: {
    order_id: string;
    success: boolean;
    awb_number?: string;
    courier_name?: string | null;
    label_url?: string | null;
    shiprocket_shipment_id?: string;
    error?: string;
  }[];
}

// Bulk-create shipments for several orders at once (each gets its own AWB).
// courierId null → auto-assign best rate per order.
export async function bulkShip(orderIds: string[], courierId: number | null): Promise<BulkShipResult> {
  const res = await client.post(`/api/admin/orders/ship-bulk`, {
    order_ids: orderIds,
    courier_id: courierId,
  });
  return res.data;
}

// Combined PDF label for several shipments (bulk "Download all labels").
export async function bulkLabels(shiprocketShipmentIds: string[]): Promise<{ label_url: string | null }> {
  const res = await client.post(`/api/admin/orders/labels/bulk`, {
    shipment_ids: shiprocketShipmentIds,
  });
  return res.data;
}

// Combined PDF label for all shipments across the selected ORDERS (Orders-list
// "Download labels"). Shipment ids are resolved server-side from the order ids.
export async function bulkLabelsByOrders(
  orderIds: string[]
): Promise<{ label_url: string | null; count: number }> {
  const res = await client.post(`/api/admin/orders/labels/bulk-by-order`, {
    order_ids: orderIds,
  });
  return res.data;
}

// Modern custom items: override the oil formula for one line item of an order.
export async function updateCustomFormula(
  id: string,
  itemId: string,
  oils: FormulaOil[]
): Promise<FormulaSaveResult> {
  const res = await client.patch(`/api/admin/orders/${id}/items/${itemId}/formula`, { oils });
  return res.data?.data ?? res.data;
}
